import { BaseGameRecorder, generateId } from '@bn-games/history-core';
import type { Color, Move } from '../game/types.js';
import { ActiveGameStore } from './ActiveGameStore.js';
import type {
  ActiveSession,
  GameRecord,
  RecordedMove,
  RecordedSeed,
} from './types.js';
import { HISTORY_SCHEMA_VERSION } from './types.js';

/**
 * Owns the in-flight `ActiveSession` for the current game. Checkers turns are
 * atomic — one `Move` (quiet slide or full capture chain) is one journal entry
 * — so the recorder is simpler than its backgammon ancestor: append on commit,
 * finalize into a `GameRecord` on game over. The base class handles session
 * id/storage write-through; this subclass only owns the per-game payload shape.
 */
export class GameRecorder extends BaseGameRecorder<ActiveSession> {
  constructor() {
    super(ActiveGameStore);
  }

  start(seed: RecordedSeed): void {
    this.setSession({
      id: generateId(),
      version: HISTORY_SCHEMA_VERSION,
      seed,
      moves: [],
      updatedAt: Date.now(),
    });
  }

  /** Append a committed move. The mover color is captured here so replay
   * doesn't need to redo turn arithmetic. */
  recordMove(move: Move, mover: Color): void {
    const entry: RecordedMove = {
      player: mover,
      from: move.from,
      to: move.to,
      path: move.path.map((s) => ({ row: s.row, col: s.col })),
      captures: move.captures.map((s) => ({ row: s.row, col: s.col })),
      promoted: move.promoted,
    };
    this.updateSession((s) => ({
      ...s,
      moves: [...s.moves, entry],
      updatedAt: Date.now(),
    }));
  }

  /** Online STATE_SYNC arrived with a wholesale board — the local move trace
   * is now stale. Reset moves but keep id + seed so the record still points to
   * the same game. */
  resetTrace(): void {
    this.updateSession((s) => ({
      ...s,
      moves: [],
      updatedAt: Date.now(),
    }));
  }

  finish(result: 'win' | 'draw' | 'abandoned', winner: Color | null): GameRecord {
    if (!this.session) {
      throw new Error('GameRecorder.finish called with no active session');
    }
    return {
      id: this.session.id,
      version: HISTORY_SCHEMA_VERSION,
      createdAt: this.session.seed.createdAt,
      finishedAt: Date.now(),
      mode: this.session.seed.mode,
      variant: this.session.seed.variant,
      players: {
        white: this.session.seed.playerWhite,
        black: this.session.seed.playerBlack,
      },
      winner,
      result,
      seed: this.session.seed,
      moves: this.session.moves,
    };
  }
}
